/**
 * nraScanPatch — shared NRA scan-response → RaoPerson canonical-slot patch.
 *
 * Extracted from IndividualNraDocumentScanTabs.tsx (v0.14.51, BACKLOG-0073)
 * so the PdwShell Stage 4 surface AND the legacy in-Stage-9 Documents-tab
 * scan can use the same canonical promotion logic. Pre-v0.14.51, the
 * PdwStage4DocsGrid wrote scan results to person.extra with a `stage4_*`
 * prefix as a temporary holding — that's now replaced by this shared
 * mapper so Stage 9 reads from canonical slots (visaType, foreignPassportNumber,
 * i94Number, etc.) without per-slot rehydration glue.
 *
 * Behavior contract:
 *   - Universal fields (DOB, citizenship country) → only patched when the
 *     person's slot is currently empty. Never clobbers officer-typed data.
 *   - Doc-specific fields → patched on every Apply regardless of prior
 *     value (the FSR explicitly chose to re-scan / overwrite).
 *   - Unknown / sparse fields → silently skipped.
 *
 * The wire-level NraScanFields shape is the v3.nra.scan response contract;
 * IndividualNraDocumentScanTabs.mapToPersonPatch is the source of truth and
 * MUST stay in lockstep with this file. (When the canonical NRA wizard
 * lands fully inside PdwShell — BACKLOG-0080+ — the in-tabs copy will be
 * deleted in favour of this shared one; until then, changes to one MUST
 * mirror to the other.)
 */
import { toNamedJsonDate } from '@/utils/jxDates';
import type { NraDocumentType, NraScanFields } from '@retail-ops/api-client';
import type { RaoPerson } from '../../../types';

/** A single retained extract field — the value + the OCR confidence + the raw
 *  pre-normalization value, so cross-reference and confirm-to-CIF can show
 *  provenance and the FSR can revert. BACKLOG-0601. */
export interface RetainedExtractField {
  value: string;
  confidence: number;
  original?: string;
}

/**
 * BACKLOG-0601 — compact a scan field union (NraScanFields or any
 * `{ key: { value, confidence, originalValue } }` shape) into a non-null map
 * for the per-type extract store `person.extra.extracts.<docType>`. Drops
 * null/empty fields so the store holds only real per-doc candidates; this is
 * the producer the §2 cross-reference + confirm-to-CIF spine consumes.
 */
export function serializeExtract(fields: Record<string, unknown>): Record<string, RetainedExtractField> {
  const out: Record<string, RetainedExtractField> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v && typeof v === 'object' && 'value' in v) {
      const val = (v as { value: unknown }).value;
      if (val != null && val !== '') {
        const orig = (v as { originalValue?: unknown }).originalValue;
        out[k] = {
          value: String(val),
          confidence: Number((v as { confidence?: number }).confidence ?? 0),
          ...(orig != null && orig !== '' ? { original: String(orig) } : {}),
        };
      }
    }
  }
  return out;
}

export function mapNraScanToPersonPatch(
  person: RaoPerson,
  fields: NraScanFields,
  docType: NraDocumentType,
): Partial<RaoPerson> {
  const patch: Partial<RaoPerson> = {};
  const extraPatch: Record<string, unknown> = {};

  // Universal — only if blank.
  if (!person.dob && fields.dateOfBirth?.value) patch.dob = toNamedJsonDate(fields.dateOfBirth.value) || fields.dateOfBirth.value;
  if (!person.countryOfCitizenship && fields.countryOfCitizenship?.value) {
    patch.countryOfCitizenship = fields.countryOfCitizenship.value;
  }

  switch (docType) {
    case 'passport':
      if (fields.foreignPassportNumber?.value) {
        patch.foreignPassportNumber = fields.foreignPassportNumber.value;
        // NRA smoke test (Bill 2026-06-12) — fornTaxId mapping REMOVED. A
        // passport does not carry a foreign tax ID; passport captures leave
        // fornTaxId untouched (manual / W-8-driven field). Mirrors the same
        // fix in captureRouting.buildPatchFromNra (the two mappers must stay
        // in lockstep per this file's header contract).
      }
      if (fields.foreignPassportCountry?.value) patch.foreignPassportCountry = fields.foreignPassportCountry.value;
      if (fields.foreignPassportIssueDt?.value) extraPatch.passportIssueDt = fields.foreignPassportIssueDt.value;
      if (fields.foreignPassportExpDt?.value) extraPatch.passportExpDt = fields.foreignPassportExpDt.value;
      break;

    case 'visa':
      if (fields.visaType?.value) patch.visaType = fields.visaType.value;
      if (fields.visaExpDt?.value) patch.visaExpDt = fields.visaExpDt.value;
      if (fields.visaIssueDt?.value) extraPatch.visaIssueDt = fields.visaIssueDt.value;
      if (fields.visaIssuePost?.value) extraPatch.visaIssuePost = fields.visaIssuePost.value;
      if (fields.visaEntries?.value) extraPatch.visaEntries = fields.visaEntries.value;
      if (fields.visaAnnotation?.value) extraPatch.visaAnnotation = fields.visaAnnotation.value;
      if (fields.visaControlNumber?.value) extraPatch.visaControlNumber = fields.visaControlNumber.value;
      break;

    case 'i94':
      if (fields.i94Number?.value) patch.i94Number = fields.i94Number.value;
      // i94ExpirationDate (BE 2026-06-12) — dedicated expiration leaf;
      // preferred over the AdmitUntil fallback (which may be "D/S").
      // Lockstep mirror of captureRouting.buildPatchFromNra.
      if (fields.i94ExpirationDate?.value) patch.i94ExpDt = fields.i94ExpirationDate.value;
      if (fields.i94AdmitUntil?.value) {
        if (fields.i94AdmitUntil.value.toUpperCase() === 'D/S') {
          extraPatch.i94AdmitUntil = 'D/S';
        } else {
          if (!patch.i94ExpDt) patch.i94ExpDt = fields.i94AdmitUntil.value;
          extraPatch.i94AdmitUntil = fields.i94AdmitUntil.value;
        }
      }
      if (fields.i94AdmitClass?.value) extraPatch.i94AdmitClass = fields.i94AdmitClass.value;
      if (fields.i94EntryDate?.value) extraPatch.i94EntryDate = fields.i94EntryDate.value;
      if (fields.i94EntryPort?.value) extraPatch.i94EntryPort = fields.i94EntryPort.value;
      break;

    case 'i20':
      if (fields.sevisId?.value) extraPatch.sevisId = fields.sevisId.value;
      if (fields.schoolName?.value) extraPatch.schoolName = fields.schoolName.value;
      if (fields.schoolCode?.value) extraPatch.schoolCode = fields.schoolCode.value;
      if (fields.programStartDt?.value) extraPatch.programStartDt = fields.programStartDt.value;
      if (fields.programEndDt?.value) extraPatch.programEndDt = fields.programEndDt.value;
      if (fields.educationLevel?.value) extraPatch.educationLevel = fields.educationLevel.value;
      if (fields.major1?.value) extraPatch.major1 = fields.major1.value;
      if (fields.major1CipCode?.value) extraPatch.major1CipCode = fields.major1CipCode.value;
      if (fields.formIssueReason?.value) extraPatch.formIssueReason = fields.formIssueReason.value;
      break;

    case 'ds2019':
      if (fields.sevisId?.value) extraPatch.sevisId = fields.sevisId.value;
      if (fields.programSponsorName?.value) extraPatch.programSponsorName = fields.programSponsorName.value;
      if (fields.programNumber?.value) extraPatch.programNumber = fields.programNumber.value;
      if (fields.exchangeVisitorCategory?.value) extraPatch.exchangeVisitorCategory = fields.exchangeVisitorCategory.value;
      if (fields.fieldCode?.value) extraPatch.fieldCode = fields.fieldCode.value;
      if (fields.twoYearResidenceFlag?.value) extraPatch.twoYearResidenceFlag = fields.twoYearResidenceFlag.value;
      break;

    case 'green-card':
      if (fields.greenCardANumber?.value) extraPatch.greenCardANumber = fields.greenCardANumber.value;
      if (fields.greenCardCategory?.value) extraPatch.greenCardCategory = fields.greenCardCategory.value;
      if (fields.greenCardExpDt?.value) extraPatch.greenCardExpDt = fields.greenCardExpDt.value;
      if (fields.greenCardResidentSince?.value) extraPatch.greenCardResidentSince = fields.greenCardResidentSince.value;
      break;

    case 'ead':
      if (fields.eadANumber?.value) extraPatch.eadANumber = fields.eadANumber.value;
      if (fields.eadCategory?.value) extraPatch.eadCategory = fields.eadCategory.value;
      if (fields.eadExpDt?.value) extraPatch.eadExpDt = fields.eadExpDt.value;
      break;

    case 'ssn':
    case 'ssn-card':
      if (fields.ssnNumber?.value) {
        extraPatch.ssnNumber = fields.ssnNumber.value;
        // For US Citizen / PR cohorts the SSN slot promotes to person.taxId.
        // Only patch when person.taxId is currently empty (don't clobber a
        // manually-entered TIN).
        if (!person.taxId) patch.taxId = fields.ssnNumber.value;
      }
      break;

    case 'primary-id': {
      // BACKLOG-0600 — US-Citizen / Permanent-Resident PRIMARY photo ID (a US
      // driver's license or US passport) scanned via the prebuilt-idDocument
      // core. Mirrors buildPatchFromAzureDiId so the two photo-ID paths
      // converge: name / gender / address onto the person (empty-only, never
      // clobber officer-typed), coded CIP (F254-258) onto person.extra.id*.
      if (!person.firstName && fields.firstName?.value) patch.firstName = fields.firstName.value;
      if (!person.middleName && fields.middleName?.value) patch.middleName = fields.middleName.value;
      if (!person.lastName && fields.lastName?.value) patch.lastName = fields.lastName.value;
      if (!person.gender && fields.sex?.value) {
        const raw = fields.sex.value.trim().toUpperCase();
        const mapped: 'Male' | 'Female' | '' = raw.startsWith('M') ? 'Male' : raw.startsWith('F') ? 'Female' : '';
        if (mapped) patch.gender = mapped;
      }
      if (!person.addr && fields.addressLine1?.value) patch.addr = fields.addressLine1.value;
      if (!person.city && fields.addressCity?.value) patch.city = fields.addressCity.value;
      if (!person.state && fields.addressState?.value) patch.state = fields.addressState.value;
      if (!person.zip && fields.addressZip?.value) patch.zip = fields.addressZip.value;
      const exNow = (person.extra ?? {}) as Record<string, unknown>;
      if (fields.pcbIdType?.value && !exNow.idType) extraPatch.idType = fields.pcbIdType.value;
      if (fields.pcbIdNumber?.value && !exNow.idNumber) extraPatch.idNumber = fields.pcbIdNumber.value;
      if (fields.pcbIdIssuer?.value && !exNow.idIssuer) extraPatch.idIssuer = fields.pcbIdIssuer.value;
      if (fields.pcbIdIssueDate?.value && !exNow.idIssueDate) extraPatch.idIssueDate = fields.pcbIdIssueDate.value;
      if (fields.pcbIdExpiryDate?.value && !exNow.idExpiryDate) extraPatch.idExpiryDate = fields.pcbIdExpiryDate.value;
      break;
    }

    // Slots without dedicated NraScanFields entries (us-drivers-license,
    // address-proof, visa-class-proof) — no field-level extraction today;
    // capturing the raw file is enough for Stage 9 to surface "captured"
    // status. Universal fields above still apply.
    default:
      break;
  }

  // BACKLOG-0601 — per-type extract store. Retain the raw extracted field set
  // under person.extra.extracts.<docType> (permanent audit trail; the producer
  // the SPEC §2 cross-ref + confirm-to-CIF + NRA-Analysis-Document spine
  // consumes). Written on EVERY scan, even when no canonical slot changed, so
  // re-scans and multi-doc cross-referencing always have per-doc candidates.
  const prevExtracts = ((person.extra ?? {}) as Record<string, unknown>).extracts as Record<string, unknown> | undefined;
  extraPatch.extracts = { ...(prevExtracts ?? {}), [docType]: serializeExtract(fields as unknown as Record<string, unknown>) };

  patch.extra = { ...(person.extra ?? {}), ...extraPatch };
  return patch;
}
